/**
 * MSAL (Microsoft Authentication Library) Configuration
 *
 * This file configures Azure Entra ID authentication using OIDC/OAuth 2.0 with PKCE flow.
 * Environment variables are automatically retrieved from AWS Parameter Store in production
 * and from .env file in development.
 *
 * @see https://docs.microsoft.com/en-us/azure/active-directory/develop/msal-js-initializing-client-applications
 */

import { LogLevel } from "@azure/msal-browser";

/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */
export const msalConfig = {
  auth: {
    clientId: process.env.REACT_APP_AZURE_CLIENT_ID || "",
    authority: `https://login.microsoftonline.com/${process.env.REACT_APP_AZURE_TENANT_ID || ""}`,
    redirectUri: process.env.REACT_APP_AZURE_REDIRECT_URI || "http://localhost:3000",
    navigateToLoginRequestUrl: true, // Allow MSAL to handle navigation automatically
  },
  cache: {
    cacheLocation: "localStorage", // Configures cache location. "sessionStorage" is more secure, but "localStorage" gives you SSO between tabs.
    storeAuthStateInCookie: false, // Set this to true for IE11 or Edge legacy browsers
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) {
          return;
        }
        switch (level) {
          case LogLevel.Error:
            console.error("MSAL Error:", message);
            return;
          case LogLevel.Info:
            console.info("MSAL Info:", message);
            return;
          case LogLevel.Verbose:
            console.log("MSAL Verbose:", message);
            return;
          case LogLevel.Warning:
            console.warn("MSAL Warning:", message);
            return;
          default:
            return;
        }
      },
      logLevel: process.env.NODE_ENV === "development" ? LogLevel.Verbose : LogLevel.Error,
    },
    allowNativeBroker: false, // Disables WAM Broker
    allowRedirectInIframe: false, // Security setting
  }
};

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit:
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
export const loginRequest = {
  scopes: [
    "openid",
    "profile",
    "email",
    // DISABLED: Uncomment when Graph API permissions are granted
    // "User.Read", // Read the signed-in user's profile
    // "User.ReadBasic.All" // Read all users' basic profiles for team member lookup
  ],
};

/**
 * Silent token request configuration.
 * Used for acquiring tokens silently in the background.
 */
export const silentRequest = {
  scopes: [
    "openid",
    "profile",
    "email",
    // DISABLED: Uncomment when Graph API permissions are granted
    // "User.Read",
    // "User.ReadBasic.All"
  ],
  forceRefresh: false, // Set this to true to skip a cached token and go to the server to get a new token
};

/**
 * API token request configuration.
 * Used for acquiring tokens to call our own API Gateway endpoints.
 * The custom scope ensures the token audience matches our app registration,
 * which the Lambda authorizer validates.
 *
 * Prerequisites: Azure Portal > App Registration > "Expose an API" must have
 * the "access" scope configured: api://{client_id}/access
 */
export const apiTokenRequest = {
  scopes: [
    `api://${process.env.REACT_APP_AZURE_CLIENT_ID}/access`,
  ],
  forceRefresh: false,
};

/**
 * Microsoft Graph API request configuration.
 * Used specifically for calling Microsoft Graph API endpoints.
 *
 * DISABLED: Uncomment when Graph API permissions are granted
 */
export const graphRequest = {
  scopes: [
    // "User.Read",  // Disabled pending Azure permissions
    // "User.ReadBasic.All"  // Disabled pending Azure permissions
  ],
};

/**
 * Session timeout configuration (10 hours / 600 minutes)
 */
export const sessionConfig = {
  timeoutMinutes: 600, // Session timeout in minutes (10 hours)
  warningMinutes: 5,  // Show warning 5 minutes before timeout
  checkIntervalSeconds: 60, // Check session status every minute
};

/**
 * Application-specific configuration
 */
export const appConfig = {
  // Routes that don't require authentication (none needed - MSAL handles redirects automatically)
  publicRoutes: [],
  // Default route after successful authentication
  defaultRoute: "/",
  // Route to redirect to after logout
  logoutRoute: "/",
};

/**
 * Error messages for authentication failures
 */
export const authErrorMessages = {
  loginRequired: "Please sign in to access this application.",
  loginFailed: "Authentication failed. Please try again.",
  tokenExpired: "Your session has expired. Please sign in again.",
  sessionTimeout: "Your session will expire soon. Please save your work.",
  networkError: "Network error occurred during authentication. Please check your connection.",
  configError: "Authentication configuration error. Please contact support.",
};

/**
 * Validate required environment variables
 */
export const validateAuthConfig = () => {
  const requiredVars = [
    'REACT_APP_AZURE_CLIENT_ID',
    'REACT_APP_AZURE_TENANT_ID',
    'REACT_APP_AZURE_REDIRECT_URI'
  ];

  const missingVars = requiredVars.filter(varName => !process.env[varName] || process.env[varName].trim() === '');

  if (missingVars.length > 0) {
    console.error('Missing required environment variables for Azure authentication:', missingVars);
    return false;
  }

  return true;
};

/**
 * Helper function to check if current route is public (doesn't require authentication)
 */
export const isPublicRoute = (pathname) => {
  return appConfig.publicRoutes.some(route =>
    pathname === route || pathname.startsWith(route)
  );
};